/**
 * Ken Burns / Fill crop-viewport math for slideshow slides.
 *
 * A crop viewport is a rectangle over the *source photo*, normalized 0..1, where `x`/`width`
 * are fractions of the photo's pixel width and `y`/`height` are fractions of the photo's pixel
 * height (see `SpApi.CropViewport`). All of the geometry below works in that normalized space,
 * or in photo/container pixel space when explicitly noted.
 */
import type { CSSProperties } from 'react';

/** End-of-animation zoom fraction (of the centered slide-aspect viewport) for the default Ken Burns effect. */
export const DEFAULT_KEN_BURNS_END_ZOOM = 0.7;

export const DEFAULT_CROP_EASING: SpApi.CropEasing = 'ease-in-out';

/** Full-photo viewport — used whenever photo/slide-aspect dimensions aren't known yet. */
const FULL_VIEWPORT: SpApi.CropViewport = { x: 0, y: 0, width: 1, height: 1 };

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}

export interface ContainRect {
    offsetX: number;
    offsetY: number;
    drawnWidth: number;
    drawnHeight: number;
}

export interface PixelRect {
    x: number;
    y: number;
    width: number;
    height: number;
}

/**
 * Rectangle (in container-pixel space) where a `photoW`×`photoH` photo is drawn inside a
 * `containerW`×`containerH` box with `object-fit: contain` (centered, scaled to fit).
 */
export function getContainRect(
    containerW: number,
    containerH: number,
    photoW: number,
    photoH: number
): ContainRect {
    if (containerW <= 0 || containerH <= 0 || photoW <= 0 || photoH <= 0) {
        return { offsetX: 0, offsetY: 0, drawnWidth: containerW, drawnHeight: containerH };
    }

    const scale = Math.min(containerW / photoW, containerH / photoH);
    const drawnWidth = photoW * scale;
    const drawnHeight = photoH * scale;

    return {
        offsetX: (containerW - drawnWidth) / 2,
        offsetY: (containerH - drawnHeight) / 2,
        drawnWidth,
        drawnHeight
    };
}

/** Maps a photo-normalized viewport to a pixel rect inside a `getContainRect` letterbox layout. */
export function viewportToContainPixels(
    viewport: SpApi.CropViewport,
    containRect: ContainRect
): PixelRect {
    return {
        x: containRect.offsetX + viewport.x * containRect.drawnWidth,
        y: containRect.offsetY + viewport.y * containRect.drawnHeight,
        width: viewport.width * containRect.drawnWidth,
        height: viewport.height * containRect.drawnHeight
    };
}

/** Largest viewport matching `slideAspectRatio` (width/height) centered on a `photoW`×`photoH` photo. */
export function getCenteredSlideAspectViewport(
    photoW: number,
    photoH: number,
    slideAspectRatio: number
): SpApi.CropViewport {
    if (photoW <= 0 || photoH <= 0 || slideAspectRatio <= 0) {
        return { ...FULL_VIEWPORT };
    }

    const photoAspectRatio = photoW / photoH;
    let boxWidthPx: number;
    let boxHeightPx: number;

    if (slideAspectRatio >= photoAspectRatio) {
        // Slide is at least as wide (relatively) as the photo — full photo width, cropped height.
        boxWidthPx = photoW;
        boxHeightPx = photoW / slideAspectRatio;
    } else {
        // Slide is relatively taller/narrower than the photo — full photo height, cropped width.
        boxHeightPx = photoH;
        boxWidthPx = photoH * slideAspectRatio;
    }

    return {
        x: (photoW - boxWidthPx) / 2 / photoW,
        y: (photoH - boxHeightPx) / 2 / photoH,
        width: boxWidthPx / photoW,
        height: boxHeightPx / photoH
    };
}

/** Scales `viewport` around its own center by `fraction` (e.g. 0.7 == zoomed in to 70% the area extents). */
export function getZoomedViewport(
    viewport: SpApi.CropViewport,
    fraction: number
): SpApi.CropViewport {
    const centerX = viewport.x + viewport.width / 2;
    const centerY = viewport.y + viewport.height / 2;
    const width = viewport.width * fraction;
    const height = viewport.height * fraction;

    return {
        x: centerX - width / 2,
        y: centerY - height / 2,
        width,
        height
    };
}

/** `getZoomedViewport` applied to the centered slide-aspect viewport — the Ken Burns "end" keyframe shape. */
export function getZoomedSlideAspectViewport(
    photoW: number,
    photoH: number,
    slideAspectRatio: number,
    fraction: number
): SpApi.CropViewport {
    return getZoomedViewport(
        getCenteredSlideAspectViewport(photoW, photoH, slideAspectRatio),
        fraction
    );
}

/**
 * Clamps `viewport` to the photo's bounds while re-locking it to `slideAspectRatio` in pixel
 * space — used when photo dimensions change (e.g. a re-crop, or the natural size resolving
 * after the metadata dimensions) so a stored keyframe can't reference out-of-bounds photo area.
 */
export function clampViewportToPhoto(
    viewport: SpApi.CropViewport,
    photoW: number,
    photoH: number,
    slideAspectRatio: number
): SpApi.CropViewport {
    if (photoW <= 0 || photoH <= 0) {
        return viewport;
    }

    let widthPx = clamp(viewport.width, 0, 1) * photoW;
    let heightPx = clamp(viewport.height, 0, 1) * photoH;

    if (slideAspectRatio > 0 && widthPx > 0 && heightPx > 0) {
        const currentAspectRatio = widthPx / heightPx;

        if (Math.abs(currentAspectRatio - slideAspectRatio) > 1e-6) {
            heightPx = widthPx / slideAspectRatio;

            if (heightPx > photoH) {
                heightPx = photoH;
                widthPx = heightPx * slideAspectRatio;
            }
        }
    }

    widthPx = clamp(widthPx, 0, photoW);
    heightPx = clamp(heightPx, 0, photoH);

    const xPx = clamp(viewport.x * photoW, 0, photoW - widthPx);
    const yPx = clamp(viewport.y * photoH, 0, photoH - heightPx);

    return {
        x: xPx / photoW,
        y: yPx / photoH,
        width: widthPx / photoW,
        height: heightPx / photoH
    };
}

/** Corner handle that resizes a crop viewport. The opposite corner stays anchored. */
export type CropResizeCorner = 'nw' | 'ne' | 'sw' | 'se';

/**
 * Resizes `viewport` by dragging `corner`, locking pixel aspect to `slideAspectRatio`.
 *
 * `deltaXPx` / `deltaYPx` are photo-pixel pointer movement. The new size is the aspect-ratio
 * rectangle closest to where the pointer is, measured from the opposite (anchored) corner, then
 * clamped so the crop stays inside the photo and at least `minWidthFraction` of the photo width.
 */
export function resizeViewportFromCorner(
    viewport: SpApi.CropViewport,
    photoW: number,
    photoH: number,
    slideAspectRatio: number,
    corner: CropResizeCorner,
    deltaXPx: number,
    deltaYPx: number,
    minWidthFraction: number
): SpApi.CropViewport {
    if (photoW <= 0 || photoH <= 0 || slideAspectRatio <= 0) {
        return viewport;
    }

    const startW = viewport.width * photoW;
    const startH = viewport.height * photoH;
    const startX = viewport.x * photoW;
    const startY = viewport.y * photoH;
    const movesRight = corner === 'ne' || corner === 'se';
    const movesDown = corner === 'se' || corner === 'sw';
    const anchorX = movesRight ? startX : startX + startW;
    const anchorY = movesDown ? startY : startY + startH;
    const widthSign = movesRight ? 1 : -1;
    const heightSign = movesDown ? 1 : -1;
    const pointerW = startW + widthSign * deltaXPx;
    const pointerH = startH + heightSign * deltaYPx;
    const aspectSq = slideAspectRatio * slideAspectRatio;
    let heightPx = (slideAspectRatio * pointerW + pointerH) / (aspectSq + 1);
    let widthPx = heightPx * slideAspectRatio;
    const minWidthPx = photoW * minWidthFraction;
    const maxWidth = Math.max(0, movesRight ? photoW - anchorX : anchorX);
    const maxHeight = Math.max(0, movesDown ? photoH - anchorY : anchorY);

    widthPx = clamp(widthPx, Math.min(minWidthPx, maxWidth), maxWidth);
    heightPx = widthPx / slideAspectRatio;

    if (heightPx > maxHeight) {
        heightPx = maxHeight;
        widthPx = heightPx * slideAspectRatio;
    }

    const xPx = movesRight ? anchorX : anchorX - widthPx;
    const yPx = movesDown ? anchorY : anchorY - heightPx;

    return {
        x: xPx / photoW,
        y: yPx / photoH,
        width: widthPx / photoW,
        height: heightPx / photoH
    };
}

/**
 * Re-clamps (or regenerates, if empty) a slide's crop keyframes for the current photo
 * dimensions / slide aspect ratio — called when the photo's natural size resolves (it may
 * differ from item metadata) or the slideshow aspect ratio changes.
 */
export function normalizeCropKeyframesForPhoto(
    keyframes: SpApi.CropViewport[],
    photoW: number,
    photoH: number,
    slideAspectRatio: number,
    cropMode: SpApi.SlideCropMode
): SpApi.CropViewport[] {
    if (cropMode === 'fit') {
        return keyframes;
    }

    if (keyframes.length === 0) {
        return cropMode === 'fill'
            ? [getCenteredSlideAspectViewport(photoW, photoH, slideAspectRatio)]
            : [
                  getCenteredSlideAspectViewport(photoW, photoH, slideAspectRatio),
                  getZoomedSlideAspectViewport(
                      photoW,
                      photoH,
                      slideAspectRatio,
                      DEFAULT_KEN_BURNS_END_ZOOM
                  )
              ];
    }

    return keyframes.map((keyframe) =>
        clampViewportToPhoto(keyframe, photoW, photoH, slideAspectRatio)
    );
}

/** Default `cropData` for a slide switching to Fill for the first time — a single centered keyframe. */
export function getDefaultFillCropData(
    photoW = 1,
    photoH = 1,
    slideAspectRatio = 16 / 9
): SpApi.SlideCropData {
    return { keyframes: [getCenteredSlideAspectViewport(photoW, photoH, slideAspectRatio)] };
}

/** Default `cropData` for a slide switching to Ken Burns for the first time — centered start, zoomed-in end. */
export function getDefaultKenBurnsCropData(
    photoW = 1,
    photoH = 1,
    slideAspectRatio = 16 / 9
): SpApi.SlideCropData {
    return {
        keyframes: [
            getCenteredSlideAspectViewport(photoW, photoH, slideAspectRatio),
            getZoomedSlideAspectViewport(
                photoW,
                photoH,
                slideAspectRatio,
                DEFAULT_KEN_BURNS_END_ZOOM
            )
        ],
        easing: DEFAULT_CROP_EASING
    };
}

function lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t;
}

function lerpViewport(a: SpApi.CropViewport, b: SpApi.CropViewport, t: number): SpApi.CropViewport {
    return {
        x: lerp(a.x, b.x, t),
        y: lerp(a.y, b.y, t),
        width: lerp(a.width, b.width, t),
        height: lerp(a.height, b.height, t)
    };
}

function applyEasing(t: number, easing: SpApi.CropEasing): number {
    if (easing === 'ease-in-out') {
        return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
    }

    return t;
}

/**
 * Interpolates `keyframes` (length >= 1) at overall `progress` (0..1), splitting the range into
 * `keyframes.length - 1` equal segments and applying `easing` within each segment.
 */
export function interpolateKeyframes(
    keyframes: SpApi.CropViewport[],
    progress: number,
    easing: SpApi.CropEasing = 'linear'
): SpApi.CropViewport {
    if (keyframes.length === 0) {
        return { ...FULL_VIEWPORT };
    }

    if (keyframes.length === 1) {
        return keyframes[0];
    }

    const clampedProgress = clamp(progress, 0, 1);
    const segmentCount = keyframes.length - 1;
    const scaledProgress = clampedProgress * segmentCount;
    const segmentIndex = Math.min(segmentCount - 1, Math.floor(scaledProgress));
    const localT = applyEasing(scaledProgress - segmentIndex, easing);

    return lerpViewport(keyframes[segmentIndex], keyframes[segmentIndex + 1], localT);
}

export interface ViewportCropPlayback {
    imageStyle: CSSProperties;
    cropTransform?: string;
    willChangeTransform?: boolean;
}

/**
 * Computes the letterboxed-image style for rendering `viewport` (Fill's single keyframe, or a
 * Ken Burns interpolated keyframe) inside a `slideBoxW`×`slideBoxH` slide box.
 *
 * - `freeze` (editing the crop for this slide, playback paused): returns the same letterboxed
 *   `contain` style the `KenBurnsViewportOverlay` draws on top of, with no crop transform.
 * - Otherwise: returns a *block* frame sized to the photo's contain rect, plus a GPU-friendly
 *   `translate(...) scale(...)` (`transform-origin: 0 0`) so the viewport region fills the
 *   slide box. The player applies this to a wrapper `<div>` and stretches the `<img>` to
 *   100%×100% inside it (absolute sizing on the replaced `<img>` itself was still painting
 *   small/top-left in the gallery player; animating left/top/width/height every frame was
 *   choppy — transform keeps the layout box stable).
 *
 * Falls back to the same letterboxed style whenever slide-box or photo dimensions aren't yet
 * known (e.g. before the image's natural size has loaded).
 */
export function getViewportCropPlayback(
    viewport: SpApi.CropViewport,
    slideBoxW: number,
    slideBoxH: number,
    photoW: number,
    photoH: number,
    freeze: boolean,
    animate = false
): ViewportCropPlayback {
    const containStyle: CSSProperties = {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain'
    };

    if (freeze || slideBoxW <= 0 || slideBoxH <= 0 || photoW <= 0 || photoH <= 0) {
        return { imageStyle: containStyle };
    }

    const containRect = getContainRect(slideBoxW, slideBoxH, photoW, photoH);
    const viewportPx = viewportToContainPixels(viewport, containRect);

    if (viewportPx.width <= 0 || viewportPx.height <= 0) {
        return { imageStyle: containStyle };
    }

    const zoom = Math.max(slideBoxW / viewportPx.width, slideBoxH / viewportPx.height);
    // Equivalent to the previous absolute left/top placement, expressed as a transform on a
    // frame that stays at the unscaled contain size (left/top 0, width/height = drawn size).
    const translateX = containRect.offsetX * zoom - viewportPx.x * zoom;
    const translateY = containRect.offsetY * zoom - viewportPx.y * zoom;
    const cropTransform = `translate(${translateX}px, ${translateY}px) scale(${zoom})`;

    return {
        imageStyle: {
            position: 'absolute',
            left: '0px',
            top: '0px',
            width: `${containRect.drawnWidth}px`,
            height: `${containRect.drawnHeight}px`,
            maxWidth: 'none',
            maxHeight: 'none',
            transform: cropTransform,
            transformOrigin: '0 0'
        },
        cropTransform,
        willChangeTransform: animate
    };
}

/** Slide-local playback progress (0..1) at `currentTime`, given the slide's global start/duration. */
export function getSlideLocalProgress(
    currentTime: number,
    slideStartMs: number,
    slideDurationMs: number
): number {
    if (slideDurationMs <= 0) {
        return 0;
    }

    return Math.max(0, Math.min(1, (currentTime - slideStartMs) / slideDurationMs));
}
